import React, { useCallback } from 'react';
import { Paper } from '@mui/material';
import CloseRounded from '@mui/icons-material/CloseRounded';
import { hydrateArtifactByRef } from '../../copilot/hooks/useArtifactHydration';
import { ChatStoreProvider, type ApprovalAction, type ChatStoreHook } from '../../../chat/core';
import { ChatPanelV2, type ChatPlace } from '../../../chat/panel';
import { ToolbarButton, type ContextChip } from '../../../ui-kit';
import { boardChat } from '../boardChat';

/**
 * Copilot, docked to the right of a board.
 *
 * The panel is handed this board's own chat, so it never sees another one.
 */
export default function BoardChatPanel({
  useStore,
  width,
  boardName,
  onSend,
  onStop,
  onSQLApproval,
  contextChips = [],
  place = boardChat,
  onClose,
  subtitle = 'Answers from this board',
  scopeLabel = 'This board',
}: {
  useStore: ChatStoreHook;
  /** px. Leave out when the panel sits in a container that sets its width, like `SidePanelDock`. */
  width?: number;
  boardName: string;
  onSend: (message: string) => void;
  onStop: () => void;
  onSQLApproval: (action: ApprovalAction, editedSql?: string, reason?: string) => void;
  /** What the answer is scoped to — the board's range and filters. */
  contextChips?: ContextChip[];
  /** Starters and welcome for this kind of board. Defaults to the cohort board's. */
  place?: ChatPlace;
  /** Shows a close button in the panel's header. */
  onClose?: () => void;
  /** Under the title. A page cut from a board says "this page", not "this board". */
  subtitle?: string;
  /** The first context chip: what the answer is drawn from. */
  scopeLabel?: string;
}) {
  const handleArtifactHydrate = useCallback(
    (artifactId: string) => {
      void hydrateArtifactByRef(artifactId, useStore.getState());
    },
    [useStore],
  );

  return (
    <Paper
      variant="outlined"
      sx={(t) => ({
        width: width ?? '100%',
        flex: width ? undefined : 1,
        flexShrink: 0,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        borderRadius: `${t.ds?.radius?.md ?? 8}px`,
        overflow: 'hidden',
      })}
    >
      <ChatStoreProvider store={useStore}>
        <ChatPanelV2
          place={place ?? boardChat}
          title={boardName}
          subtitle={subtitle}
          onSend={onSend}
          onStop={onStop}
          onSQLApproval={onSQLApproval}
          onArtifactHydrate={handleArtifactHydrate}
          contextChips={{ base: scopeLabel, chips: contextChips }}
          headerActions={
            onClose ? (
              <ToolbarButton
                variant="ghost"
                icon={<CloseRounded fontSize="small" />}
                onClick={onClose}
                aria-label="Close chat"
                tooltip="Close chat"
              />
            ) : undefined
          }
        />
      </ChatStoreProvider>
    </Paper>
  );
}
